import {
  DealRoomContactsReq,
  DealRoomContactsResp,
  DealRoomContractDocument,
  DealRoomReq,
  DealRoomResp,
  DealRoomShareAccessResponse,
  DealRoomShareRequest,
  IOpportunityWithQuotes,
  IStorage,
  QuoteStatusEnum,
  RecordShareEngagement,
  RecordShareEngagementSchema,
  StorageTypeEnum,
} from '@monetize/types/app';
import { addMonthsCustom, toDateOnly } from '@monetize/utils/core';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, apiPut, apiUpload } from '../../api/axios';

export const dealRoomServiceKeys = {
  base: ['deal-room'] as const,
  dealRoomById: (id: string) => [...dealRoomServiceKeys.base, id] as const,
  attachmentById: (dealRoomId: string, storageId) =>
    [
      ...dealRoomServiceKeys.dealRoomById(dealRoomId),
      'attachements',
      storageId,
    ] as const,
  engagement: (recordId: string) =>
    [...dealRoomServiceKeys.base, 'engagement', recordId] as const,
  shareLinks: (dealRoomId: string) =>
    [...dealRoomServiceKeys.dealRoomById(dealRoomId), 'share-links'] as const,
};

export function useDealRoomById(
  dealRoomId: string,
  options: Partial<UseQueryOptions<DealRoomResp>> = {},
) {
  return useQuery({
    queryKey: [...dealRoomServiceKeys.dealRoomById(dealRoomId)],
    queryFn: () =>
      apiGet<DealRoomResp>(`api/dealRooms/${dealRoomId}`).then(
        (res) => res.data,
      ),
    enabled: !!dealRoomId,
    refetchOnWindowFocus: false,
    ...options,
  });
}

export async function doGetDealRoomById(dealRoomId: string) {
  const resp = await apiGet<DealRoomResp>(`api/dealRooms/${dealRoomId}`);
  return resp.data;
}

export function useCreateDealRoom(
  options: Partial<
    UseMutationOptions<DealRoomResp, unknown, { opportunityId: string }>
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<DealRoomResp, unknown, { opportunityId: string }>({
    mutationFn: async ({ opportunityId }: { opportunityId: string }) => {
      const { data: opportunity } = await apiGet<IOpportunityWithQuotes>(
        `/api/opportunities/${opportunityId}`,
      );

      const ALLOWED_STATUES = new Set([
        QuoteStatusEnum.DRAFT,
        QuoteStatusEnum.APPROVED,
      ]);
      const quotes =
        opportunity.quotes?.filter(({ status }) =>
          ALLOWED_STATUES.has(status),
        ) || [];
      const expirationDate = toDateOnly(addMonthsCustom(new Date(), 1));

      return apiPost<DealRoomResp>(`/api/dealRooms`, {
        opportunityId: opportunity.id,
        quotes: quotes.map(({ id, name }, i) => ({
          quoteId: id,
          customName: name,
          position: i + 1,
        })),
        legalEntityId: quotes.find((quote) => quote.legalEntityId)
          ?.legalEntityId,
        accountId: opportunity.accountId,
        requiresEsign: false,
        expirationDate,
      }).then(({ data }) => {
        queryClient.setQueryData(
          dealRoomServiceKeys.dealRoomById(data.id),
          data,
        );
        return data;
      });
    },
    ...options,
  });
}

export function useUpdateDealRoom(
  options: Partial<
    UseMutationOptions<
      DealRoomResp,
      unknown,
      { payload: DealRoomReq; dealRoomId: string }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    DealRoomResp,
    unknown,
    { payload: DealRoomReq; dealRoomId: string }
  >({
    mutationFn: ({ payload, dealRoomId }) =>
      apiPut<DealRoomResp>(`/api/dealRooms/${dealRoomId}`, payload, {
        axiosConfig: {
          skipNullifyStrings: true,
        },
      }).then(({ data }) => {
        queryClient.setQueryData(
          dealRoomServiceKeys.dealRoomById(data.id),
          data,
        );
        return data;
      }),
    ...restOptions,
  });
}

export const doUploadAttachment = async (dealRoomId: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const res = await apiUpload<IStorage>(
    `/api/dealRooms/${dealRoomId}/attachments`,
    formData,
  );
  return res.data;
};

export const doUploadDealRoomDocument = async (
  dealRoomId: string,
  {
    storageType,
    documentUrl,
    file,
  }: {
    storageType: StorageTypeEnum;
    documentUrl?: string;
    file?: File;
  },
) => {
  const formData = new FormData();
  file && formData.append('file', file);
  const data: {
    storageType: StorageTypeEnum;
    documentUrl?: string;
  } = {
    storageType,
  };
  if (documentUrl) {
    data.documentUrl = documentUrl;
  }
  formData.append(
    'data',
    new Blob([JSON.stringify(data)], {
      type: 'application/json',
    }),
  );

  const res = await apiUpload<{
    contractDocuments: DealRoomContractDocument[];
  }>(
    `/api/dealRooms/${dealRoomId}/contractDocuments`,
    formData,
    undefined,
    undefined,
    true,
  );

  return res.data.contractDocuments;
};

export const doDeleteDealRoomDocument = async (
  dealRoomId: string,
  type: StorageTypeEnum,
) => {
  const res = await apiDelete<{
    contractDocuments: DealRoomContractDocument[];
  }>(`/api/dealRooms/${dealRoomId}/contractDocuments?type=${type}`);

  return res.data.contractDocuments;
};

export function useGetAttachmentByStorageId(
  {
    dealRoomId,
    storageId,
  }: {
    dealRoomId: string;
    storageId: string;
  },
  options: Partial<UseQueryOptions<IStorage>> = {},
) {
  return useQuery({
    queryKey: [...dealRoomServiceKeys.attachmentById(dealRoomId, storageId)],
    queryFn: () =>
      apiGet<IStorage>(
        `/api/dealRooms/${dealRoomId}/attachments/${storageId}`,
        { params: { attachContent: true } },
      ).then((res) => res.data),
    enabled: !!dealRoomId,
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useSaveDealRoomContacts(
  options: Partial<
    UseMutationOptions<
      DealRoomContactsResp,
      unknown,
      { payload: DealRoomContactsReq; dealRoomId: string }
    >
  > = {},
) {
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    DealRoomContactsResp,
    unknown,
    { payload: DealRoomContactsReq; dealRoomId: string }
  >({
    mutationFn: ({ payload, dealRoomId }) =>
      apiPut<any>(`/api/dealRooms/${dealRoomId}/contacts`, payload).then(
        ({ data }) => data,
      ),
    onSuccess: (data, variables, context) => {},
    ...restOptions,
  });
}

export function useGetDealRoomRecordShareEngagements<
  SelectData = RecordShareEngagement[],
>(
  recordId: string,
  options: Partial<
    UseQueryOptions<RecordShareEngagement[], unknown, SelectData>
  > = {},
) {
  return useQuery<RecordShareEngagement[], unknown, SelectData>({
    queryKey: dealRoomServiceKeys.engagement(recordId),
    queryFn: () =>
      apiGet<RecordShareEngagement[]>(`/api/share/engagement/${recordId}`).then(
        (res) => RecordShareEngagementSchema.array().parse(res.data),
      ),
    ...options,
  });
}

export function useGetDealRoomShareAccess<
  SelectData = DealRoomShareAccessResponse[],
>(
  dealRoomId: string,
  options: Partial<
    UseQueryOptions<DealRoomShareAccessResponse[], unknown, SelectData>
  > = {},
) {
  return useQuery<DealRoomShareAccessResponse[], unknown, SelectData>({
    queryKey: dealRoomServiceKeys.shareLinks(dealRoomId),
    queryFn: () =>
      apiGet<DealRoomShareAccessResponse[]>(
        `/api/dealRooms/${dealRoomId}/shareLinks`,
      ).then((res) => res.data),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useCreateDealRoomShareLink(
  dealRoomId: string,
  options: Partial<
    UseMutationOptions<
      DealRoomShareAccessResponse,
      unknown,
      DealRoomShareRequest
    >
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation<
    DealRoomShareAccessResponse,
    unknown,
    DealRoomShareRequest
  >({
    mutationFn: ({ expiresAt }) => {
      return apiPost<DealRoomShareAccessResponse>(
        `/api/dealRooms/${dealRoomId}/generateShareLink`,
        { expiresAt },
      ).then((res) => res.data);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: dealRoomServiceKeys.shareLinks(dealRoomId),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useRevokeDealRoomShare(
  options: Partial<
    UseMutationOptions<void, unknown, { dealRoomId: string; shareId: string }>
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, { dealRoomId: string; shareId: string }>({
    mutationFn: ({ shareId, dealRoomId }) =>
      apiDelete(`/api/share/${shareId}/revoke`).then((res) => res.data),
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: dealRoomServiceKeys.shareLinks(variables.dealRoomId),
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function doShareDealRoom(dealRoomId: string) {
  return apiPost<DealRoomResp>(`/api/dealRooms/${dealRoomId}/share`, null, {
    axiosConfig: {
      keepEmptyPayload: true,
    },
  }).then((res) => res.data);
}

export function doEditDealRoom(dealRoomId: string) {
  return apiPost<DealRoomResp>(`/api/dealRooms/${dealRoomId}/edit`).then(
    (res) => res.data,
  );
}
